import React from 'react';
import dayjs from 'dayjs';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { cn } from '../../components/ui/_cn';
import FinButton from './finance/fin-button';
import COPY from './finance/connect-finance-copy';
import { formatDay } from './finance/connect-finance-tables';

// Frame 4a's banner, above the status strip. Two urgencies, one sentence:
// past due means Stripe is about to stop the account, so it gets the red
// treatment and the date; merely due is amber and says the same thing
// without the pressure (spec 2026-09-22 §9).
//
// Hidden when both lists are empty — a banner that is always on screen is
// wallpaper, and this one has to be believed the day it appears.
//
// The requirement codes themselves never reach the screen: they are Stripe's
// vocabulary ("individual.id_number"), and the button hands over to the
// component that asks for them in plain words.
//
// `onReview` is undefined on a Restricted account: that page shows
// onboarding, not the record, `managing` has nothing to flip, and a button
// with nowhere to send the press would be inert rather than useful — the
// onboarding form already on screen is what collects the requirements
// there, so this banner hides its own button rather than doing nothing.
const ActionRequiredBanner = ({ status, onReview }) => {
  const copy = COPY.restaurant;
  const pastDue = status?.pastDue || [];
  const currentlyDue = status?.currentlyDue || [];
  if (pastDue.length === 0 && currentlyDue.length === 0) return null;

  const urgent = pastDue.length > 0;
  // formatDay is the house's own local-zone convention (connect-finance-
  // tables.js) rather than a second date format invented here.
  const deadline = status?.currentDeadline && dayjs(status.currentDeadline).isValid() ? status.currentDeadline : null;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3.5 rounded-xl border px-[18px] py-3.5',
        urgent ? 'border-fin-red bg-fin-red-pale' : 'border-fin-amber bg-fin-amber-pale',
      )}
    >
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
          urgent ? 'bg-fin-red/15' : 'bg-fin-amber/15',
        )}
      >
        <ExclamationTriangleIcon
          className={cn('h-4 w-4 stroke-[2.2]', urgent ? 'text-fin-red' : 'text-fin-amber')}
          aria-hidden="true"
        />
      </span>
      <div className="min-w-0 flex-1">
        <p className={cn('text-[14px] font-bold', urgent ? 'text-fin-red' : 'text-fin-amber')}>
          {copy.actionRequiredTitle}
        </p>
        <p className={cn('mt-0.5 text-[13px]', urgent ? 'text-fin-red' : 'text-fin-amber')}>
          {copy.actionRequiredBody}
          {deadline ? (
            <>
              {' Due by '}
              <b className="font-bold">{formatDay(deadline)}</b>.
            </>
          ) : null}
        </p>
      </div>
      {onReview ? <FinButton variant="primary" onClick={onReview}>Review requirements</FinButton> : null}
    </div>
  );
};

export default ActionRequiredBanner;
